import { useRef, useState } from "react";

interface Props {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  onChange: (value: number) => void;
}

const TICK_COUNT = 12; // purely visual ruler marks - doesn't need to divide evenly into (max-min)/step

/**
 * A timeline-scrubber-style control (ruler ticks, a filled progress
 * track, a draggable playhead) instead of a native <input type="range">
 * - this is the ONE shared component every numeric slider in the app
 * renders through (zoom, font size, stretch, stroke width/opacity,
 * outline offsets, etc.), so redesigning it here is what makes the
 * change apply everywhere at once. Props are unchanged from before,
 * so every existing call site keeps working with no other edits.
 *
 * Direction is intentionally mirrored (RIGHT = min, LEFT = max) to
 * match RTL reading direction - "start" of the scale sits on the
 * right, same side Arabic text starts from, instead of the LTR-only
 * left-to-right convention a plain <input type="range"> always uses.
 */
export function SliderControl({ label, value, min, max, step = 1, unit = "px", onChange }: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  // percent still means "value's position between min (0) and max
  // (100)" - only WHERE that's drawn on screen is mirrored (anchored
  // from the right edge instead of the left) further down.
  const percent = max > min ? Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100)) : 0;

  const valueFromClientX = (clientX: number): number => {
    const track = trackRef.current;
    if (!track) return value;
    const rect = track.getBoundingClientRect();
    // Reversed from a plain LTR slider: ratio is measured from the
    // RIGHT edge, so dragging towards the right approaches min and
    // dragging towards the left approaches max.
    const ratio = rect.width > 0 ? Math.max(0, Math.min(1, (rect.right - clientX) / rect.width)) : 0;
    const raw = min + ratio * (max - min);
    const stepped = Math.round(raw / step) * step;
    // Round away float noise (e.g. 0.1 + 0.2 issues) before clamping,
    // so displayed values land cleanly on step boundaries.
    const precision = step < 1 ? String(step).split(".")[1]?.length ?? 2 : 0;
    const rounded = Number(stepped.toFixed(precision));
    return Math.max(min, Math.min(max, rounded));
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    onChange(valueFromClientX(e.clientX));
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    onChange(valueFromClientX(e.clientX));
  };

  const endDrag = () => setDragging(false);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm text-tg-hint">{label}</label>
        <span
          className={`text-sm font-semibold tabular-nums px-2 py-0.5 rounded-md transition-colors duration-150 ${
            dragging ? "bg-tg-button text-tg-buttonText" : "text-tg-text"
          }`}
          dir="ltr"
        >
          {value}
          {unit}
        </span>
      </div>

      <div
        ref={trackRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        role="slider"
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        className="relative h-9 flex items-center cursor-pointer touch-none select-none no-native-drag"
      >
        {/* Ruler ticks - a subtle backdrop reinforcing the "timeline"
            feel; taller ticks every 4th mark. Lighting them up is
            measured from the RIGHT (100 - tickPercent) to match the
            mirrored fill/handle below - the ticks nearest the right
            edge light up first as the value increases. */}
        <div className="absolute inset-x-1 top-1/2 -translate-y-1/2 flex justify-between pointer-events-none">
          {Array.from({ length: TICK_COUNT + 1 }).map((_, i) => {
            const tickPercent = (i / TICK_COUNT) * 100;
            const isMajor = i % 4 === 0;
            return (
              <span
                key={i}
                className={`w-px rounded-full transition-colors duration-150 ${
                  100 - tickPercent <= percent ? "bg-tg-button/60" : "bg-white/15"
                }`}
                style={{ height: isMajor ? 11 : 6 }}
              />
            );
          })}
        </div>

        {/* Track base + filled progress - anchored to the RIGHT edge
            and growing leftward as the value increases, instead of
            the standard left-anchored growth. */}
        <div className="absolute inset-x-0 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div
            className="absolute inset-y-0 right-0 h-full bg-tg-button transition-[width] duration-75"
            style={{ width: `${percent}%` }}
          />
        </div>

        {/* Playhead handle - positioned from the right edge to match */}
        <div
          className={`absolute w-5 h-5 rounded-full bg-tg-button border-2 border-white shadow-md transition-transform duration-150 ${
            dragging ? "scale-125" : "scale-100"
          }`}
          style={{ right: `calc(${percent}% - 10px)` }}
        />
      </div>
    </div>
  );
}
